import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bike, Mail, SlidersHorizontal } from "lucide-react";
import { MaskedLines, Reveal, SectionHeading } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { fetchProducts } from "@/lib/api";

const STEPS = [
  { icon: SlidersHorizontal, title: "Configure", body: "Pick display and battery — price updates live." },
  { icon: Mail, title: "We invoice", body: "We confirm stock, then email your manual invoice." },
  { icon: Bike, title: "Pay & ride", body: "$170 flat covers shipping to your door or Auckland pickup. Full installation by us is $200 flat." },
];

export default function Shop() {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const combos = products ? products.filter((p) => p.badge === "Combo Kit") : [];
  const accessories = products ? products.filter((p) => p.badge === "Accessory") : [];

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <section className="py-16 sm:py-20">
        <p className="eyebrow mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          Shop — Conversion Kits & Accessories
        </p>
        <h1
          className="font-display font-black tracking-tight text-ink leading-[0.95]"
          style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}
          data-testid="shop-heading"
        >
          <MaskedLines
            lines={[
              <span key="a">PICK YOUR</span>,
              <span key="b">
                <span className="text-volt">COMBO.</span>
              </span>,
            ]}
          />
        </h1>
        <Reveal delay={0.35}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
            Two combos, built to order — 2000W at 52V or 3000W at 72V. You
            choose the controller, display and battery, and we build it out
            with our supplier. Everything included; the only extras are $170
            shipping and option upgrades.
          </p>
        </Reveal>

        <Reveal delay={0.45}>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="flex items-start gap-3 rounded-xl border border-black/10 bg-surface p-4" data-testid={`shop-step-${i}`}>
                <s.icon className="mt-0.5 h-5 w-5 shrink-0 text-volt" strokeWidth={2.2} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {String(i + 1).padStart(2, "0")} — {s.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-600">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <Section title="Combos" count={combos.length} testid="shop-combos">
        <div className="grid gap-6 md:grid-cols-2" data-testid="shop-grid">
          {products === null && <p className="text-zinc-500">Loading kits…</p>}
          {products && products.length === 0 && (
            <p className="text-zinc-500" data-testid="shop-empty">
              Kits are being stocked — check back shortly.
            </p>
          )}
          {combos.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 0.1} eager={i === 0} />
          ))}
        </div>
      </Section>

      <Section title="Accessories" count={accessories.length} testid="shop-accessories" className="mt-20">
        <div className="grid gap-6 md:grid-cols-2" data-testid="shop-accessories-grid">
          {accessories.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 0.1} />
          ))}
        </div>
        <Reveal delay={0.1} className="mt-6">
          <p className="text-xs leading-relaxed text-zinc-500 max-w-xl">
            Accessories ship with the same $170 flat rate NZ-wide — or add them
            to a combo order and they ride along in the same box.
          </p>
        </Reveal>
      </Section>

      <Reveal delay={0.1} className="mt-20">
        <div className="flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-black/10 bg-surface p-8 sm:p-10">
          <div>
            <p className="eyebrow mb-3">Finishing touches</p>
            <h3 className="max-w-lg font-display text-2xl font-extrabold tracking-tight text-ink">
              Footpegs, grips, lighting and more — ship free with any combo.
            </h3>
          </div>
          <Link
            to="/accessories"
            data-testid="shop-accessories-link"
            className="group inline-flex items-center gap-2 rounded-[3px] bg-volt px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
          >
            Browse Accessories
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

function Section({ title, children, testid, className = "", count }) {
  return (
    <section className={className} data-testid={testid}>
      <Reveal>
        <div className="mb-8 flex items-baseline gap-4">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            {title}
          </h2>
          {count > 0 && (
            <span className="font-mono text-xs text-zinc-400">
              {String(count).padStart(2, "0")} items
            </span>
          )}
        </div>
      </Reveal>
      {children}
    </section>
  );
}
